import './App.scss';

import { YjsDebugPanel } from './components/Debug/YjsDebugPanel';
import WindowControls from './components/WindowControls/WindowControls';

// Load debug utilities in development using centralized dev mode
import { devOnly, IS_DEV } from './utils/devMode';
devOnly(() => import('./utils/debugCleanup'));

// Run one-time IDB migrations (cleanup of legacy databases, format changes)
import('./services/idb/idbMigrations').then(m => m.runIDBMigrations()).catch(() => {});

// Start periodic IDB storage monitoring (background, non-blocking)
import('./services/idb/storageMonitor').then(m => m.startStorageMonitor()).catch(() => {});

// ============================================
// UNIFIED REGISTRY INITIALIZATION
// ============================================

import { entityTypeRegistry } from './services/entityTypeRegistry';
import { toolRegistry } from './tools/registry';
import { initializeAllCommands } from './stores/commandRegistryStore';
import { appRegistry } from './services/appRegistryService';

async function loadBundleCommandsAsync(): Promise<void> {
  try {
    const { default: fetchService } = await import('./services/fetchService');
    const { API_BASE_URL } = await import('./api/config');
    const { registerBundleCommands } = await import('./stores/commandRegistryStore');

    const result = await fetchService(`${API_BASE_URL}/entities?entityType=AppProject&limit=50`);
    const entities: any[] = Array.isArray(result) ? result : (result.entities ?? []);

    const bundles: Array<{ bundleId: string; bundleName: string; tools: any[] }> = [];
    for (const entity of entities) {
      const tools = entity.skeleton?.tools;
      if (!Array.isArray(tools) || tools.length === 0) continue;
      bundles.push({
        bundleId: entity._id,
        bundleName: entity.name,
        tools: tools.filter((t: any) => t.name && t.description && t.handlerUrl),
      });
    }

    if (bundles.length > 0) {
      registerBundleCommands(bundles);
    }
  } catch (err: any) {
    console.warn('[App] Failed to load bundle commands (non-fatal):', err.message);
  }
}

async function initializeRegistries() {
  try {
    await Promise.all([
      Promise.resolve().then(() => {
        entityTypeRegistry.loadEntityTypes();
      }).catch((error: Error) => {
        console.warn('[App] Entity type registry initialization failed (non-fatal):', error);
      }),
      
      appRegistry.initialize().catch((error: Error) => {
        console.warn('[App] App registry initialization failed (non-fatal):', error);
      }),
      
      toolRegistry.loadAllTools().catch((error: Error) => {
        console.warn('[App] Tool registry initialization failed (non-fatal):', error);
      }),
    ]);
  } catch (error) {
    console.error('[App] Registry initialization failed:', error);
  } finally {
    // Always initialize commands regardless of whether individual registries failed.
    // Without this, a failed entity type or tool load would leave the command registry
    // empty and break all command execution (e.g. space_openEntity).
    initializeAllCommands();

    // Async, non-blocking: register commands from installed App Bundles
    // Runs after synchronous built-in registration; built-ins always win conflicts
    loadBundleCommandsAsync().catch(() => {}); // errors already logged inside

    // Initialize hot reload for generated apps
    try {
      const { hotReloadService } = await import('./apps/AppBuilderApp/services/hotReloadService');
      hotReloadService.initialize();
    } catch (hotReloadError) {
      console.warn('[App] Hot reload initialization failed (non-fatal):', hotReloadError);
    }
  }
}

import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SessionExpiredModal from './components/SessionExpiredModal/SessionExpiredModal';
import { useSessionMonitor } from './hooks/useSessionMonitor';
import OSSignupModal from './components/modals/OSSignupModal';
import useUserStore from './stores/userStore';

// Lazy load org payment gate
const OrgPaymentGate = React.lazy(() => import('./components/Billing/OrgPaymentGate'));

// Import dataspace context store for payment gate
import { useDataspaceContextStore } from './stores/dataspaceContextStore';

// Import all apps - let webpack tree-shake unused ones
const LLMChefApp = React.lazy(() => import('apps/LLMChef/LLMChef'));
const ListenApp = React.lazy(() => import('apps/ListenApp/ListenApp'));
const LibraryApp = React.lazy(() => import('apps/LibraryApp/LibraryApp').then(m => ({ default: m.LibraryApp })));
const StoreApp = React.lazy(() => import('apps/StoreApp/StoreApp'));
const QuickLauncherApp = React.lazy(() => import('apps/QuickLauncherApp/QuickLauncherApp'));
const MailApp = React.lazy(() => import('apps/MailApp/MailApp').then(m => ({ default: m.MailApp })));
const MessagingApp = React.lazy(() => import('apps/MessagingApp/MessagingApp').then(m => ({ default: m.MessagingApp })));
const PasswordVaultApp = React.lazy(() => import('apps/PasswordVaultApp/PasswordVaultApp').then(m => ({ default: m.PasswordVaultApp })));
const InboxApp = React.lazy(() => import('apps/InboxApp/InboxApp').then(m => ({ default: m.InboxApp })));

function StandaloneMailAppWrapper() {
  return <MailApp windowId="standalone-mail" spaceId="default" onClose={() => {}} />;
}
function StandaloneMessagingAppWrapper() {
  return <MessagingApp windowId="standalone-messaging" spaceId="default" onClose={() => {}} />;
}

// Lazy load large layout components
const UpgradeModal = React.lazy(() => import('./components/UpgradeModal/UpgradeModal').then(m => ({ default: m.UpgradeModal })));
const InviteFriendModalLazy = React.lazy(() => import('./components/InviteFriendModal/InviteFriendModal').then(m => ({ default: m.InviteFriendModal })));
const ToolConsentModal = React.lazy(() => import('./components/ToolConsentModal').then(m => ({ default: m.ToolConsentModal })));
const UserSettingsModal = React.lazy(() => import('./components/Space/SystemMenuButton/UserSettings/UserSettingsModal').then(m => ({ default: m.UserSettingsModal })));
const GUILayout = React.lazy(() => import('./components/GUILayout/GUILayout'));
import { SystemContextProvider } from './components/GUILayout/SystemContext';
const ScrollToTop = React.lazy(() => import('./components/ScrollToTop/ScrollToTop'));
const StandaloneAppLayout = React.lazy(() => import('./components/Space/layouts/StandaloneAppLayout'));
const ElectronAppLayout = React.lazy(() => import('./components/Space/layouts/ElectronAppLayout'));
const ToastContainer = React.lazy(() => import('./components/Toast/Toast').then(m => ({ default: m.ToastContainer })));
import ToastStack from './components/ToastStack/ToastStack';
const UpdateToast = React.lazy(() => import('./components/UpdateToast/UpdateToast').then(m => ({ default: m.UpdateToast })));
import { getMixpanelConfig } from './config/mixpanel';
import mixpanelService from './services/analytics/mixpanelService';
import ga4Service from './services/analytics/ga4Service';
import { userTrackingService } from './services/users/userTrackingService';
import { createFileLogger } from './utils/logger';
import { parseJwtPayload } from './utils/jwtUtils';
import { enableNavigationPrevention } from './utils/navigationPrevention';
import { isElectron } from './utils/electron';
import { perf } from './utils/perfInstrumentation';

import { ThemeProvider } from './contexts/ThemeContext';
const SelfHostedLicenseGate = React.lazy(() => import('./components/SelfHostedLicenseGate'));
// Lazy load marketing pages for better performance
const NSFWAIWriter = React.lazy(() => import('pages/marketing/products/NSFWAIWriter/NSFWAIWriter'));
const GrantWriters = React.lazy(() => import('pages/marketing/products/GrantWriters/GrantWriters'));
const PrivateWorkspace = React.lazy(() => import('pages/marketing/products/PrivateWorkspace/PrivateWorkspace'));
const CreativeFreedom = React.lazy(() => import('pages/marketing/products/CreativeFreedom/CreativeFreedom'));
const FanfictionWriter = React.lazy(() => import('pages/marketing/products/FanfictionWriter/FanfictionWriter'));
// SelfPublishedBook landing page is SSR-rendered by the content-renderer at /addons/self-published-book
const SelfPublishedBookRedirect: React.FC = () => {
  React.useEffect(() => {
    window.location.replace('https://rivereditor.com/addons/self-published-book');
  }, []);
  return null;
};
const BookAddonPurchasePage = React.lazy(() => import('pages/marketing/products/BookAddonPurchase/BookAddonPurchasePage'));
const RFPWriters = React.lazy(() => import('pages/marketing/products/RFPWriters/RFPWriters'));
const AcademicWriters = React.lazy(() => import('pages/marketing/products/AcademicWriters/AcademicWriters'));
const HNWWriters = React.lazy(() => import('pages/marketing/products/HNWWriters/HNWWriters'));
const PromptLibraryApp = React.lazy(() => import('apps/PromptLibraryApp/PromptLibraryApp'));
const SpaceCreationPage = React.lazy(() => import('./pages/SpaceCreationPage'));
import { useAuth } from './hooks/useAuth';
import { useAuth0 } from '@auth0/auth0-react';
import { captureRefCode } from './services/utm/utmService';
import { useGlobalCmdKShortcut } from './hooks/useGlobalCmdKShortcut';
import { useAmbientSuggestions } from './hooks/useAmbientSuggestions';
import { useGlobalNewWindowShortcut } from './hooks/useGlobalNewWindowShortcut';
import { useGlobalEntitySearchShortcut } from './hooks/useGlobalEntitySearchShortcut';
import { useGlobalElectronTabShortcuts } from './hooks/useGlobalElectronTabShortcuts';
import { useGlobalSpacePanelShortcuts } from './hooks/useGlobalSpacePanelShortcuts';
import { useDefaultsInitializer } from './hooks/useDefaultsInitializer';
import { useQuickEntryContextPush } from './hooks/useQuickEntryContextPush';
const ContactsApp = React.lazy(() => import('apps/ContactsApp/ContactsApp').then(m => ({ default: m.ContactsApp })));
const ShowcasePage = React.lazy(() => import('./pages/ShowcasePage'));
const ShowcaseDocumentPage = React.lazy(() => import('./pages/ShowcaseDocumentPage'));
const PublicBookViewer = React.lazy(() => import('./pages/PublicBookViewer'));
const GoogleDriveCallbackPage = React.lazy(() => import('./pages/GoogleDriveCallbackPage'));
const GmailOAuthCallback = React.lazy(() => import('./pages/GmailOAuthCallback'));
const BroadcastUnsubscribePageLazy = React.lazy(() =>
  import('./pages/BroadcastUnsubscribePage/BroadcastUnsubscribePage').then(m => ({ default: m.BroadcastUnsubscribePage }))
);
const BlogAuthCallback = React.lazy(() => import('./pages/BlogAuthCallback'));
const OnboardingIntroDebugButton = React.lazy(() => import('./components/Debug/OnboardingIntroDebugButton'));

// Load all components - webpack will tree-shake unused ones
const Home = React.lazy(() => import('components/Home/Home'));
const HomeSpaceWrapper = React.lazy(() => import('./pages/HomeSpaceWrapper'));
const ProtectedRoute = React.lazy(() => import('./components/ProtectedRoute'));
const AdminProtectedRoute = React.lazy(() => import('./components/AdminProtectedRoute'));
// Lazy load for better initial bundle size
const SelfhostedLoginPage = React.lazy(() => import('./pages/SelfhostedLoginPage'));

const SuccessPage = React.lazy(() => import('./pages/SuccessPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const OrgPaymentSuccessPage = React.lazy(() => import('./pages/OrgPaymentSuccessPage'));
const InvitationAcceptPage = React.lazy(() => import('./pages/InvitationAcceptPage'));
const DownloadsPage = React.lazy(() => import('./pages/DownloadsPage'));
const SpacePageWrapper = React.lazy(() => import('./pages/SpacePageWrapper'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const LoginPage = React.lazy(() => import('pages/LoginPage'));
const ElectronOnboardingPage = React.lazy(() => import('./pages/ElectronOnboarding'));
const ElectronFirstRunPage = React.lazy(() => import('./pages/ElectronFirstRun/ElectronFirstRun'));
const AgentConversationPage = React.lazy(() => import('./components/Space/AgentChat/AgentChat'));
const AgentsHome = React.lazy(() => import('./pages/AgentsHome/AgentsHome'));
const OnboardingCardPreview = React.lazy(() => import('./pages/OnboardingCardPreview/OnboardingCardPreview'));
const ToolCardPreview = React.lazy(() => import('./pages/ToolCardPreview/ToolCardPreview'));
const SelfHostedOnboardingPage = React.lazy(() => import('./pages/SelfHostedOnboarding'));

// Standalone app components - load all apps
const BookkeeperApp = React.lazy(() => import('./apps/BookkeeperApp/BookkeeperApp'));
const DocumentAccessWrapper = React.lazy(() => import('./apps/DocsApp/components/DocumentAccessWrapper'));
const AgentPanelRoot = React.lazy(() => import('./agents/AgentPanelRoot'));
const AgentLayout = React.lazy(() => import('./components/GUILayout/AgentLayout'));
const BankApp = React.lazy(() => import('apps/BankApp/components/BankApp'));
const RiverDriveApp = React.lazy(() => import('apps/RiverDriveApp/RiverDriveApp'));

// Load all apps
const TasksApp = React.lazy(() => import('apps/TasksApp/TasksApp'));
const AutomationsApp = React.lazy(() => import('apps/AutomationsApp/AutomationsApp'));
const ChatApp = React.lazy(() => import('apps/ChatApp/ChatApp'));
const LoginApp = React.lazy(() => import('./LoginApp'));

const AdminDashboard = React.lazy(() => import('./pages/internal/AdminDashboard'));
const AdminAnalyticsView = React.lazy(() => import('./pages/internal/AdminAnalyticsView'));
const AdminLLMUsageTable = React.lazy(() => import('./pages/internal/AdminLLMUsageTable'));
const AdminSetup = React.lazy(() => import('./pages/internal/AdminSetup'));

// Marketing pages - lazy load for better initial bundle size
const RiverDocsLanding = React.lazy(() => import('./pages/marketing/RiverDocsLanding'));
const MediaGenApp = React.lazy(() => import('apps/MediaGenApp/MediaGenApp'));
const CollegeEssayLanding = React.lazy(() => import('pages/marketing/college-essay/CollegeEssayLanding'));
const River4 = React.lazy(() => import('./pages/marketing/products/River4/River4'));
const HumanFirst = React.lazy(() => import('./pages/marketing/products/HumanFirst/HumanFirst'));
const ConfigureRiver1Page = React.lazy(() => import('./pages/marketing/products/RiverBox1/ConfigureRiver1Page'));
const AIHumanizerLanding = React.lazy(() => import('./pages/marketing/AIHumanizerLanding'));
const HumanizerOnboarding = React.lazy(() => import('./pages/onboardingFlows/HumanizerTool/HumanizerOnboarding'));
const NSFWOnboarding = React.lazy(() => import('./pages/onboardingFlows/NSFWWriters/NSFWOnboarding'));
const BlogCreatorOnboarding = React.lazy(() => import('./pages/onboardingFlows/BlogCreator/BlogCreatorOnboarding'));
const SEOBlogCreatorOnboarding = React.lazy(() => import('./pages/onboardingFlows/SEOBlogCreator/SEOBlogCreatorOnboarding'));
const SEOOptimizerOnboarding = React.lazy(() => import('./pages/onboardingFlows/SEOOptimizer/SEOOptimizerOnboarding'));
const ContentPolisherOnboarding = React.lazy(() => import('./pages/onboardingFlows/ContentPolisher/ContentPolisherOnboarding'));
const ImageInserterOnboarding = React.lazy(() => import('./pages/onboardingFlows/ImageInserter/ImageInserterOnboarding'));
const TemplateOnboarding = React.lazy(() => import('./pages/onboardingFlows/TemplateOnboarding/TemplateOnboarding'));

// Platform Landing & General Onboarding
const RiverSpacesLanding = React.lazy(() => import('./pages/marketing/RiverSpacesLanding/RiverSpacesLanding'));
const RiverOnboarding = React.lazy(() => import('./pages/Onboarding/RiverOnboarding'));

// Product Pages
const ProductsPage = React.lazy(() => import('./pages/marketing/products/ProductsPage'));
const AiPersonalComputer = React.lazy(() => import('./pages/marketing/products/AiPersonalComputer/AiPersonalComputer'));
const PersonalAIPage = React.lazy(() => import('./pages/marketing/products/PersonalAIPage/PersonalAIPage'));
const RiverConfigFlow = React.lazy(() => import('./pages/RiverConfigFlow/RiverConfigFlow'));
const WorkspacePage = React.lazy(() => import('./pages/marketing/products/WorkspacePage/WorkspacePage'));
const WorkLanding = React.lazy(() => import('./pages/WorkLanding/WorkLanding'));
const WorkLandingUpgradePage = React.lazy(() => import('./pages/WorkLanding/WorkLandingUpgradePage'));
const ConciergeRiverLanding = React.lazy(() => import('./pages/ConciergeRiverLanding/ConciergeRiverLanding'));
const TasksAppPage = React.lazy(() => import('./pages/marketing/products/TasksPage/TasksPage'));
const OSSignupFlow = React.lazy(() => import('./pages/OSSignup/OSSignupFlow'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));

// Company Pages
const AboutPage = React.lazy(() => import('./pages/marketing/company/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/marketing/company/ContactPage'));
const PrivacyPage = React.lazy(() => import('./pages/marketing/company/PrivacyPage'));
const PricingPage = React.lazy(() => import('./pages/marketing/PricingPage/PricingPage'));
const AvatarPage  = React.lazy(() => import('./pages/AvatarPage'));
const AvatarPage2 = React.lazy(() => import('./pages/AvatarPage2'));
const AvatarPage3 = React.lazy(() => import('./pages/AvatarPage3'));
const TermsPage = React.lazy(() => import('./pages/marketing/company/TermsPage'));

// Enterprise Pages
const AcademicMedicinePINew = React.lazy(() => import('./pages/marketing/enterprise/science/AcademicMedicinePINew'));
const LawyerDocsEditor = React.lazy(() => import('./pages/marketing/enterprise/lawyers/LawyerDocsEditor'));
const ConsultantDocsEditor = React.lazy(() => import('./pages/marketing/enterprise/consultants/ConsultantDocsEditor'));

// Use Case Pages
const PreLawDeadlinePanic = React.lazy(() => import('./pages/marketing/use-cases/PreLawDeadlinePanic'));

// Converter Pages
const PdfToWordConverter = React.lazy(() => import('./pages/marketing/convert/pdf-to-word/PdfToWordConverter'));
const ManuscriptDiscovery = React.lazy(() => import('./pages/marketing/manuscript-feedback/ManuscriptDiscovery'));
const ManuscriptAnalysisProgress = React.lazy(() => import('./pages/marketing/manuscript-feedback/ManuscriptAnalysisProgress'));
const ChatGPTToWordConverter = React.lazy(() => import('./pages/marketing/convert/chatgpt-to-word/ChatGPTToWordConverter'));
const MarkdownToWordConverter = React.lazy(() => import('./pages/marketing/convert/markdown-to-word/MarkdownToWordConverter'));

// Tool Pages
const YouTubeScriptCreator = React.lazy(() => import('./pages/marketing/tools/youtube-script-creator/YouTubeScriptCreator'));
const AIProductDescriptionGenerator = React.lazy(() => import('./pages/marketing/tools/ai-product-description-generator/AIProductDescriptionGenerator'));
const ManuscriptFeedbackLanding = React.lazy(() => import('./pages/marketing/manuscript-feedback/ManuscriptFeedbackLanding'));
const CopyeditorLanding = React.lazy(() => import('./pages/marketing/tools/copyeditor/CopyeditorLanding'));
const EssayReviewLanding = React.lazy(() => import('./pages/marketing/essay-review/EssayReviewLanding'));
const WorkflowLanding = React.lazy(() => import('./components/WorkflowLanding'));
const WorkflowInputPage = React.lazy(() => import('./components/WorkflowInputPage/WorkflowInputPage'));
const ToolsLandingPage = React.lazy(() => import('./pages/marketing/ToolsLandingPage'));
const BlogsLandingPage = React.lazy(() => import('./pages/marketing/BlogsLandingPage'));
const BlogLanding = React.lazy(() => import('./components/BlogLanding'));
const SEOBlogsLanding = React.lazy(() => import('./pages/marketing/SEOBlogsLanding'));
const GettingStartedPage = React.lazy(() => import('./pages/marketing/getting-started/GettingStartedPage'));
const GhostwriterLanding = React.lazy(() => import('./pages/marketing/use-cases/ghostwriter/GhostwriterLanding'));
const GrantWriterLanding = React.lazy(() => import('./pages/marketing/use-cases/grant-writer/GrantWriterLanding'));
const MarketersLanding = React.lazy(() => import('./pages/marketing/use-cases/marketers/MarketersLanding'));
const FactCheckerLanding = React.lazy(() => import('./pages/marketing/fact-checker/FactCheckerLanding'));

// Other Apps - conditional
const IngestWizardApp = React.lazy(() => import('apps/IngestWizard/IngestWizard'));
const NotesMarkdownApp = React.lazy(() => import('apps/NotesMarkdownApp/NotesMarkdownApp').then(m => ({ default: m.NotesMarkdownApp })));
const StandaloneEntityPage = React.lazy(() => import('./pages/StandaloneEntityPage'));

// Wrapper that reads ?view= from the URL and passes it to ContactsApp
function StandaloneContactsApp() {
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view') || undefined;
  return (
    <ContactsApp
      windowId="standalone-contacts"
      spaceId="default"
      initialView={viewParam as any}
      onClose={() => {}}
    />
  );
}

// Wrapper component with key to force remount when space ID changes
function SpacePageWrapperWithKey() {
  const log = createFileLogger('SPACE_PAGE_WRAPPER_WITH_KEY', { enabled: false });
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  log.log('[SpacePageWrapperWithKey] 🔑 Rendering with key:', {
    spaceId: id,
    timestamp: new Date().toISOString()
  });
  
  // ✨ INSTANT NAVIGATION: Add transition class to enable animations
  const isFromHome = (location.state as any)?.isPendingSpace === true;
  const isFromCreationPage = (location.state as any)?.isFromCreationPage === true;
  
  React.useEffect(() => {
    if (isFromHome) {
      // Add class to enable entrance animation
      document.body.classList.add('space-entering-from-home');
      
      // Remove after animation completes
      const timer = setTimeout(() => {
        document.body.classList.remove('space-entering-from-home');
      }, 1500);
      
      return () => {
        clearTimeout(timer);
        document.body.classList.remove('space-entering-from-home');
      };
    } else if (isFromCreationPage) {
      document.body.classList.add('space-entering-from-creation');
      
      // Remove after animation completes
      const timer = setTimeout(() => {
        document.body.classList.remove('space-entering-from-creation');
      }, 500);
      
      return () => {
        clearTimeout(timer);
        document.body.classList.remove('space-entering-from-creation');
      };
    }
  }, [isFromHome, isFromCreationPage]);
  
  
  // Use space ID as key to force remount only when space actually changes
  return <SpacePageWrapper key={id} />;
}

function RootHomeApp() {
  const { docId } = useParams<{ docId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Custom navigation handler that doesn't remount the layout
  const handleDocNavigation = useCallback((newDocId: string) => {
    navigate(`/d/${newDocId}`, { replace: false });
  }, [navigate]);
  
  // Handle close - go back to launcher
  const handleClose = useCallback(() => {
    navigate('/', { replace: false });
  }, [navigate]);
  
  // If we're on /d/:docId route, show the document
  if (location.pathname.startsWith('/d/') && docId) {
    return (
      <Suspense fallback={<Loader message="Loading document..." />}>
        <DocumentAccessWrapper 
          windowId="root-docs" 
          spaceId="default" 
          onClose={handleClose} 
          title="Docs"
        />
      </Suspense>
    );
  }
  
  // Otherwise show QuickLauncher
  return (
    <QuickLauncherApp 
      windowId="root-quicklauncher" 
      spaceId="default" 
      onClose={() => {}} 
      title="Quick Launcher"
      onNavigateToDoc={handleDocNavigation}
    />
  );
}

/**
 * /r/:code — Single affiliate referral entry point.
 * Stores the code in localStorage (first-touch-wins) then redirects:
 *   - Authenticated users → / (app loads with AffiliateOfferBanner)
 *   - Unauthenticated users → /signup (ref code survives in localStorage)
 */
function AffiliateRefRoute() {
  const { code } = useParams<{ code: string }>();
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (code) {
      captureRefCode(code);
    }
  }, [code]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate('/', { replace: true });
    } else {
      navigate('/signup', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return null;
}

function RootRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const log = createFileLogger('ROOT_ROUTE', { enabled: false });

  const electronEnv = isElectron();

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    setIsAuthenticated(!!authToken);
    log.log('[QL_FLOW] 🏁 RootRoute: Set isAuthenticated:', !!authToken);
  }, []);

  log.log('[QL_FLOW] 🏁 RootRoute rendered:', { pathname: location.pathname, isAuthenticated });

  if (electronEnv) {
    return <Navigate to="/login" replace />;
  }

  // Removed auto-redirect to /new - users now stay on landing page or navigate manually

  // Allow document routes to render even without authentication (for public documents)
  if (location.pathname.startsWith('/d/')) {
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering RootHomeApp (doc route)');
    return <RootHomeApp />;
  }

  // For SEO: Render landing page immediately on initial load (before auth check completes)
  // This prevents Google from seeing a redirect. Auth check will happen in background.
  if (isAuthenticated === null) {
    // Check if we're on riversystem.ai first (synchronous check)
    if (window.location.hostname.includes('riversystem.ai')) {
      log.log('[QL_FLOW] 🏁 RootRoute: Rendering SelfhostedLoginPage (initial load)');
      return <SelfhostedLoginPage />;
    }
    
    // For public landing page, render immediately for better SEO
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering RiverDocsLanding (initial load, auth pending)');
    return <RiverDocsLanding />;
  }

  if (isAuthenticated) {
    // Authenticated users see landing page with access to their workspace
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering landing page (authenticated)');
    if (window.location.hostname.includes('riversystem.ai')) {
      log.log('[QL_FLOW] 🏁 RootRoute: Rendering SelfhostedLoginPage');
      return <SelfhostedLoginPage />;
    }
    return <RiverDocsLanding />;
  }

  if (window.location.hostname.includes('riversystem.ai')) {
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering SelfhostedLoginPage');
    return <SelfhostedLoginPage />;
  }

  log.log('[QL_FLOW] 🏁 RootRoute: Rendering RiverDocsLanding');
  return <RiverDocsLanding />;
}

interface LoaderProps {
  message?: string;
}

// Wave path and motion preference are constants — computed once at module load,
// not on every render.
const LOADER_WIDTH = 240;
const LOADER_HEIGHT = 80;
const LOADER_STROKE_WIDTH = 6;
const LOADER_COLOR = "#4a89dc";
const LOADER_SEGMENTS = 4;
const LOADER_AMPLITUDE = 14;
const LOADER_DURATION = 1.6;
const LOADER_MID_Y = LOADER_HEIGHT / 2;
const LOADER_WAVE_LENGTH = LOADER_WIDTH / LOADER_SEGMENTS;
const LOADER_EXTENDED_WIDTH = LOADER_WIDTH + LOADER_WAVE_LENGTH;
const LOADER_STEPS = Math.max(100, LOADER_EXTENDED_WIDTH * 2);

const LOADER_WAVE_PATH = (() => {
  let path = `M 0 ${LOADER_MID_Y}`;
  for (let i = 1; i <= LOADER_STEPS; i++) {
    const x = (i / LOADER_STEPS) * LOADER_EXTENDED_WIDTH;
    const angle = (x / LOADER_WAVE_LENGTH) * Math.PI * 2;
    path += ` L ${x} ${LOADER_MID_Y + Math.sin(angle) * LOADER_AMPLITUDE}`;
  }
  return path;
})();

const LOADER_REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function Loader({ message }: LoaderProps = {}) {
  // Always show a default message to prevent layout shift
  const displayMessage = message || 'Loading...';

  return (
    <div className="space-loading">
      <svg
        width={LOADER_WIDTH}
        height={LOADER_HEIGHT}
        viewBox={`0 0 ${LOADER_WIDTH} ${LOADER_HEIGHT}`}
        role="img"
        aria-label="Loading"
        style={{ display: "block", overflow: "hidden" }}
      >
        <defs>
          <clipPath id="loaderClip">
            <rect x="0" y="0" width={LOADER_WIDTH} height={LOADER_HEIGHT} />
          </clipPath>
        </defs>

        <g clipPath="url(#loaderClip)">
          <path
            d={LOADER_WAVE_PATH}
            fill="none"
            stroke={LOADER_COLOR}
            strokeWidth={LOADER_STROKE_WIDTH + 4}
            strokeLinecap="round"
            opacity={0.2}
          >
            {!LOADER_REDUCED_MOTION && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0,0;${-LOADER_WIDTH / LOADER_SEGMENTS},0`}
                dur={`${LOADER_DURATION}s`}
                repeatCount="indefinite"
              />
            )}
          </path>

          <path
            d={LOADER_WAVE_PATH}
            fill="none"
            stroke={LOADER_COLOR}
            strokeWidth={LOADER_STROKE_WIDTH}
            strokeLinecap="round"
          >
            {!LOADER_REDUCED_MOTION && (
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`0,0;${-LOADER_WIDTH / LOADER_SEGMENTS},0`}
                dur={`${LOADER_DURATION}s`}
                repeatCount="indefinite"
              />
            )}
          </path>
        </g>
      </svg>
      <div style={{
        marginTop: '16px',
        fontSize: '14px',
        color: '#666',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        animation: 'fadeIn 0.3s ease-in',
        minHeight: '21px'
      }}>
        {displayMessage}
      </div>
    </div>
  );
}

// Single-subscription wrapper so OrgPaymentGate only fires one Zustand subscriber.
function OrgPaymentGateWrapper() {
  const paymentGate = useDataspaceContextStore(state => state.paymentGate);
  return (
    <OrgPaymentGate
      isOpen={paymentGate.isOpen}
      orgId={paymentGate.orgId || ''}
      orgName={paymentGate.orgName || ''}
      seatCount={paymentGate.seatCount}
      onPaymentStarted={() => useDataspaceContextStore.getState().hidePaymentGate()}
      onCancel={() => {
        useDataspaceContextStore.getState().hidePaymentGate();
        useDataspaceContextStore.getState().switchToPersonal();
      }}
    />
  );
}

export function App() {
  // Contextual AI modal — opened programmatically by Ambient Intelligence Surface
  const { GlobalCmdKModal, openWithAmbientMessage } = useGlobalCmdKShortcut();

  // Ambient Intelligence Surface — reacts to world events and surfaces suggestions
  const { AmbientBubble } = useAmbientSuggestions({ onAct: openWithAmbientMessage });

  // ✅ Enable Electron tab/navigation shortcuts (Cmd+T, Cmd+W, Cmd+[, Cmd+], Ctrl+Tab)
  useGlobalElectronTabShortcuts();

  // ✅ Enable global shortcuts: Cmd+\ sidebar, Cmd+J chat, Cmd+H home, Cmd+D drive, Cmd+N new thread, Cmd+Enter new window, Cmd+K search
  useGlobalSpacePanelShortcuts();
  useGlobalNewWindowShortcut();
  const { GlobalEntitySearchModal } = useGlobalEntitySearchShortcut();
  
  // ✅ Initialize default apps and tools for user
  useDefaultsInitializer();

  // Push current Space context to Electron main process for Quick Entry (Ctrl+Space)
  useQuickEntryContextPush();

  // Listen for navigation commands from the Quick Entry window via main process IPC
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!isElectron()) return;
    const handler = (_event: unknown, { path }: { path: string }) => {
      navigate(path);
    };
    (window as any).electron?.ipcRenderer?.on('app:navigate', handler);
    return () => {
      (window as any).electron?.ipcRenderer?.removeListener('app:navigate', handler);
    };
  }, [navigate]);

  const location = useLocation(); // still needed for admin-route check on mount
  const log = createFileLogger('APP', { enabled: false });
  const { user } = useAuth();

  // Guard: run systemStore init (orgs + context + entityStore bootstrap) at most
  // once per authenticated user. Navigation changes must NOT re-trigger it.
  const systemStoreInitialized = React.useRef(false);
  
  // Settings modal state for URL deep linking (when on pages without SystemMenuButton)
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState<string | undefined>();
  const [settingsInitialSection, setSettingsInitialSection] = React.useState<string | undefined>();
  
  // ✨ Initialize promo on first login
  const promoInitialized = React.useRef(false);
  
  React.useEffect(() => {
    if (user && !promoInitialized.current) {
      promoInitialized.current = true;
      import('./stores/promoStore').then(({ default: usePromoStore }) => {
        usePromoStore.getState().markFirstShown();
      }).catch(() => {});
    }
  }, [user]);
  
  // Invite-a-friend modal — triggered from userStore when free user hits 100 credits
  const showInviteFriendModal = useUserStore(state => state.showInviteFriendModal);
  const setShowInviteFriendModal = useUserStore(state => state.setShowInviteFriendModal);
  // Session monitoring for token expiration
  const { isSessionExpiredModalOpen, handleLogin } = useSessionMonitor();

  // Initialize user tracking service on mount. Must run before any analytics calls.
  // Called exactly once; the analytics effect below does NOT repeat this call.
  React.useEffect(() => {
    perf.increment('userTrackingInitCount');
    userTrackingService.init();

    // Skip token-based identification for OAuth callback popups — they share
    // localStorage with the main window and must not modify it.
    const isOAuthCallback = window.location.pathname.includes('/integrations/') && window.location.pathname.includes('/callback');

    log.log('[App] 🔍 CHECKING OAUTH CALLBACK:', {
      pathname: window.location.pathname,
      isOAuthCallback,
    });

    if (isOAuthCallback) {
      log.log('[App] ✅ SKIPPING TOKEN VALIDATION FOR OAUTH CALLBACK PAGE');
      return;
    }

    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('authToken');
    const tokenToUse = idToken || accessToken;

    if (tokenToUse) {
      try {
        const payload = parseJwtPayload(tokenToUse);

        if (!payload) {
          throw new Error('Invalid JWT structure or encoding');
        }

        if (payload.sub) {
          userTrackingService.setUser(payload.sub);

          const hasValidEmail = payload.email && payload.email.trim() !== '';
          const hasValidName = payload.name && payload.name.trim() !== '';

          if (hasValidEmail || hasValidName) {
            userTrackingService.updateUserInfo({
              email: hasValidEmail ? payload.email : undefined,
              name: hasValidName ? payload.name : undefined,
              userId: payload.sub,
            });
          }
        } else {
          throw new Error('Invalid token payload - missing sub field');
        }
      } catch (error) {
        log.error('Error parsing existing auth token for analytics:', error);
        log.warn('[App] Token parsing failed but keeping token - LoginGUI will validate');
      }
    }
  }, []);

  // Global billing polling loop — deferred to idle time since it's not needed before UI is interactive.
  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let unsubscribe: (() => void) | null = null;
    let lastPlan: any = null;
    let currentInterval: number = 60000;
    let idleHandle: number | ReturnType<typeof setTimeout> | null = null;
    
    const initBillingPolling = async () => {
      const useUserStore = (await import('./stores/userStore')).default;
      const { isFreePlan } = await import('./utils/planUtils');
      
      // Helper to calculate credits remaining
      const getCreditsRemaining = (state: any) => {
        const { currentBill } = state;
        if (!currentBill) return null;
        const usedCents = currentBill.charges?.usedCost || 0;
        const maxCents = currentBill.charges?.monthlyAllowance || currentBill.plan?.monthlyFreeCredits || 50;
        return Math.max(0, maxCents - usedCents);
      };
      
      // Polling interval: free/basic check frequently (overage detection + budget exhaustion).
      // Paid plans (payg/standard/premium) are primarily updated via YJS billing_refresh after each
      // chat stream — this poll is just a background safety net at a lower frequency.
      const getPollingInterval = (plan: string | null, creditsRemaining: number | null) => {
        const isPaidPlan = plan && plan !== 'free' && plan !== 'basic';
        if (isPaidPlan) return 300000; // 5 minutes — safety net only
        if (creditsRemaining !== null && creditsRemaining < 50) {
          log.info('[BILLING_POLLING] Low credits detected, polling every 30s');
          return 30000;
        }
        return 60000;
      };
      
      // Helper to start/restart polling interval
      const startPolling = () => {
        const state = useUserStore.getState();
        const planName = state.plan?.plan ?? null;
        const creditsRemaining = getCreditsRemaining(state);
        const newInterval = getPollingInterval(planName, creditsRemaining);
        
        // Clear existing interval if interval duration changed
        if (intervalId && currentInterval !== newInterval) {
          log.info('[BILLING_POLLING] Interval duration changed, restarting', {
            old: `${currentInterval}ms`,
            new: `${newInterval}ms`
          });
          clearInterval(intervalId);
          intervalId = null;
        }
        
        // Create new interval if needed
        if (!intervalId) {
          currentInterval = newInterval;
          intervalId = setInterval(() => {
            const currentState = useUserStore.getState();
            if (currentState.plan) {
              currentState.fetchCurrentBill();
              
              // After fetch, check if we need to adjust interval (credit level may have dropped)
              const newCreditsRemaining = getCreditsRemaining(currentState);
              const requiredInterval = getPollingInterval(currentState.plan.plan, newCreditsRemaining);
              if (requiredInterval !== currentInterval) {
                startPolling();
              }
            } else {
              if (intervalId) clearInterval(intervalId);
              intervalId = null;
            }
          }, newInterval);
        }
      };
      
      // Subscribe to store changes and check if plan changed
      unsubscribe = useUserStore.subscribe((state) => {
        const { plan, fetchCurrentBill } = state;
        
        // Only react if plan actually changed
        if (plan !== lastPlan) {
          lastPlan = plan;
          
          // Clear existing interval if any
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          
          if (plan) {
            log.info(`[BILLING_POLLING] ${plan.plan} plan detected - starting billing poll`);
            fetchCurrentBill();
            startPolling();
          }
        }
      });
      
      // Trigger initial check with current plan state
      const { plan, fetchCurrentBill } = useUserStore.getState();
      lastPlan = plan;
      if (plan) {
        log.info(`[BILLING_POLLING] Initial ${plan.plan} plan detected - fetching bill`);
        fetchCurrentBill();
        startPolling();
      }
    };
    
    if ('requestIdleCallback' in window) {
      idleHandle = requestIdleCallback(() => initBillingPolling(), { timeout: 5000 });
    } else {
      idleHandle = setTimeout(() => initBillingPolling(), 3000);
    }
    
    return () => {
      if (idleHandle != null) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(idleHandle as number);
        else clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
      }
      if (intervalId) clearInterval(intervalId);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // When the tab regains focus, reconnect any WebSocket providers that Chrome
  // may have silenced by throttling background-tab timers. y-websocket handles
  // reconnection internally via maxBackoffTime, but its timers don't fire
  // while the tab is suspended — this kick-starts them immediately.
  React.useEffect(() => {
    const onFocus = () => {
      if (document.hidden) return;
      import('./services/crdt/crdtServiceWS').then(({ crdtServiceWS }) => {
        crdtServiceWS.reconnectAll();
      }).catch(() => {});
      import('./stores/orgYDocStore').then(({ reconnectOrgYDocStore }) => {
        reconnectOrgYDocStore();
      }).catch(() => {});
      // Refresh the org list so newly-joined teams appear without a full page reload,
      // but only if the last successful load was more than 5 minutes ago. Tab switches
      // are frequent and org membership rarely changes between them.
      import('./stores/dataspaceContextStore').then(({ useDataspaceContextStore }) => {
        const { lastOrgLoadAt, loadUserOrgs } = useDataspaceContextStore.getState();
        const FIVE_MINUTES = 5 * 60 * 1000;
        if (!lastOrgLoadAt || Date.now() - lastOrgLoadAt > FIVE_MINUTES) {
          loadUserOrgs();
        }
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, []);

  // Initialize SystemStore globally (manages spaces and dataspace context).
  // Runs once per authenticated user — route navigation must NOT re-trigger it.
  React.useEffect(() => {
    // Cleanup handle for the notification Y.js subscription created inside this effect.
    let unsubscribeNotifications: (() => void) | null = null;

    const initSystemStore = async () => {
      perf.increment('systemStoreInitCount');

      // Skip on admin routes — they don't need entity/context bootstrap.
      if (location.pathname.startsWith('/admin')) {
        log.info('[SYSTEM_STORE_INIT] ⏭️ Skipping init for admin route');
        return;
      }

      const authToken = localStorage.getItem('authToken');
      if (!authToken || !user) return;

      // Once-guard: don't repeat org-load + context-fetch on navigation.
      if (systemStoreInitialized.current) {
        log.info('[SYSTEM_STORE_INIT] Already initialized — skipping repeat run');
        return;
      }
      systemStoreInitialized.current = true;

      perf.increment('systemStoreInitWithUser');
      try {
        const [
          { default: useSystemStore },
          { default: fetchService },
          { API_BASE_URL },
        ] = await Promise.all([
          import('./stores/systemStore'),
          import('./services/fetchService'),
          import('./api/config'),
        ]);

        // Kick off notifications Y.js in parallel with the network round-trips —
        // it only needs user.sub, which is already available from Auth0.
        const earlyUserId = user.sub ?? '';
        if (earlyUserId) {
          import('./stores/notificationsYjsStore').then(({ initNotificationsYjsStore }) => {
            initNotificationsYjsStore(earlyUserId);
          }).catch(() => {});
        }

        // Load orgs and fetch authoritative context in parallel — they are independent.
        const [, backendContextResult] = await Promise.allSettled([
          useSystemStore.getState().actions.loadUserOrgs(),
          fetchService(`${API_BASE_URL}/user/context`, { method: 'GET' }),
        ]);

        log.info('[SYSTEM_STORE_INIT] ✅ Organizations loaded');

        // Sync authoritative context into both stores atomically.
        try {
          if (backendContextResult.status === 'rejected') throw backendContextResult.reason;
          const backendContext = backendContextResult.value;

          log.info('[SYSTEM_STORE_INIT] 📥 Fetched context from backend:', {
            type: backendContext.type,
            orgId: backendContext.org_id,
            orgName: backendContext.org_name
          });

          const normalizedContext = {
            type: backendContext.type,
            orgId: backendContext.org_id || null,
            orgName: backendContext.org_name || null
          };

          // Write both stores in the same tick so they are never diverged.
          const { useDataspaceContextStore: dsStore } = await import('./stores/dataspaceContextStore');
          useSystemStore.setState({ currentContext: normalizedContext });
          dsStore.setState({ currentContext: normalizedContext });
          log.info('[SYSTEM_STORE_INIT] ✅ Both context stores synced from backend');
        } catch (contextError) {
          log.warn('[SYSTEM_STORE_INIT] Failed to fetch context from backend, using cached:', contextError);
        }

        log.info('[SYSTEM_STORE_INIT] ✅ System store initialized');

        // Align entityStore dataspace key with the authoritative context.
        const context = useSystemStore.getState().currentContext;
        const expectedKey = context.orgId ?? 'personal';
        const useEntityStore = (await import('./stores/entityStore')).default;
        const currentKey = useEntityStore.getState().currentDataspaceKey;

        if (currentKey !== expectedKey) {
          log.warn(`[DATASPACE_INIT] Key mismatch: context=${expectedKey} store=${currentKey} — resetting`);
          useEntityStore.setState({
            currentDataspaceKey: expectedKey,
            entities: [],
            initialized: false,
            lifecycle: 'uninitialized'
          });
        }
      } catch (error) {
        log.error('[SYSTEM_STORE_INIT] Failed to initialize system store:', error);
      }

      const useEntityStore = (await import('./stores/entityStore')).default;

      if (!useEntityStore.getState().initialized) {
        try {
          const useSystemStore = (await import('./stores/systemStore')).default;
          const context = useSystemStore.getState().currentContext;

          const dataspaceRoomKey = context.orgId
            ? `org:${context.orgId}`
            : `user:${user.sub}`;

          const dataspaceKey = context.orgId ?? 'unknown';
          useEntityStore.setState({ currentDataspaceKey: dataspaceKey });
          log.info('[ENTITY_STORE_INIT] Set currentDataspaceKey:', dataspaceKey);

          // Start entity-system Y.Doc before entityStore.initialize() so the IDB
          // 'synced' event fires before the 10s safety net.
          const dataspaceId = context.orgId || (user.sub ?? '');
          const { initEntitySystemYjsStore } = await import('./stores/entitySystemYjsStore');
          if (dataspaceId) initEntitySystemYjsStore(dataspaceId);
          log.info('[ENTITY_STORE_INIT] Kicked off entity-system IDB for dataspaceId:', dataspaceId);

          // Subscribe to real-time notifications via Y.js WebSocket room.
          const userId = user.sub ?? '';
          if (userId) {
            const {
              initNotificationsYjsStore,
              subscribeNotificationsYjs,
            } = await import('./stores/notificationsYjsStore');
            const { notificationService } = await import('./services/notificationService');

            initNotificationsYjsStore(userId);

            // Capture the unsubscribe so the cleanup function can tear it down.
            unsubscribeNotifications = subscribeNotificationsYjs((id, action, entry) => {
              if (!entry) return;

              if ((entry as any).type === 'job_status') {
                const ev = entry as any;
                log.info(
                  `[App] job_status via notification channel: job=${ev.jobId} ` +
                  `status=${ev.status} cancelRequested=${ev.cancelRequested}`
                );
                import('./apps/AutomationsApp/store/automationsStore').then(m => {
                  const stores = m.getAllAutomationsStores();
                  stores.forEach(store => {
                    store.getState().applyJobStatusEvent({
                      jobId: ev.jobId,
                      status: ev.status,
                      cancelRequested: ev.cancelRequested,
                      progress: ev.progress,
                    });
                  });
                });
                return;
              }

              if ((entry as any).type === 'toast') {
                notificationService.showToastFromEntry(entry as any);
                const threadId = (entry as any).data?.threadId;
                const status = (entry as any).priority === 'high' ? 'failed' : 'succeeded';
                if (threadId) {
                  window.dispatchEvent(
                    new CustomEvent('thread:agentRunStatus', { detail: { threadId, status } })
                  );
                }
                return;
              }

              const e = entry as any;
              const isFailed = e.priority === 'high' || e.priority === 'urgent'
                || e.message?.toLowerCase().includes('fail');

              notificationService.upsert({
                id,
                type: isFailed ? 'automation_error' : 'automation_complete',
                title: isFailed ? 'Automation failed' : 'Automation complete',
                message: e.message || '',
                status: isFailed ? 'failed' : 'succeeded',
                read: e.read === true,
                timestamp: e.sentAt ? new Date(e.sentAt).getTime() : Date.now(),
                jobId: e.data?.jobId,
                actionUrl: e.actionUrl ?? (e.data?.threadId ? `/space?thread=${e.data.threadId}` : undefined),
              });

              log.debug('[NOTIFICATIONS] Live notification → popup', id, e.priority);
              notificationService.showToastFromEntry(e);
              const threadId = e.data?.threadId;
              if (threadId) {
                window.dispatchEvent(new CustomEvent('thread:agentRunStatus', {
                  detail: { threadId, status: isFailed ? 'failed' : 'succeeded' },
                }));
              }
            });

            log.info('[NOTIFICATIONS_INIT] Kicked off notifications Y.js for userId:', userId.slice(-6));
          }

          await useEntityStore.getState().initialize(dataspaceRoomKey);
          log.info('[ENTITY_STORE_INIT] ✅ Initialized with dataspaceRoomKey:', dataspaceRoomKey);
        } catch (error) {
          log.error('[ENTITY_STORE_INIT] Failed:', error);
        }
      }
    };

    initSystemStore();

    return () => {
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
        unsubscribeNotifications = null;
      }
    };
  // location.pathname intentionally excluded: this effect must not re-run on navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Parallel non-blocking init: app registry, registries, and admin status.
  // These are independent of each other and of the systemStore init above.
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      initializeRegistries();
      return;
    }

    Promise.all([
      import('./stores/appRegistryStore').then(({ useAppRegistryStore }) =>
        useAppRegistryStore.getState().initializeApps()
      ).catch(() => {}),

      initializeRegistries(),

      import('./stores/userStore').then(({ default: s }) =>
        s.getState().fetchAdminStatus()
      ).catch(() => {}),
    ]).catch(() => {});
  }, []);

  // Register MCP tools for any connected servers once entityStore is ready.
  // Subscribes to entityStore lifecycle so we don't fire before entities are loaded.
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const runMCPInit = async () => {
      try {
        const useEntityStore = (await import('./stores/entityStore')).default;
        const { mcpAdapter } = await import('./services/mcp/mcpAdapter');
        const { mcpToolRegistry } = await import('./services/mcp/MCPToolRegistry');
        const { registerSelfContainedCommands } = await import('./stores/commandRegistryStore');

        const entities = useEntityStore.getState().entities;
        const connected = entities.filter(
          (e) =>
            e.entityType === 'MCPConnection' &&
            e.skeleton?.status === 'connected' &&
            e.skeleton?.manifest
        );

        if (connected.length === 0) {
          // Only count as a bug if entities weren't loaded yet. If lifecycle is 'ready'
          // and connected is empty, the user simply has no connected MCP servers — fine.
          if (useEntityStore.getState().lifecycle !== 'ready') {
            perf.increment('mcpInitSkippedEmptyEntities');
          }
          return;
        }
        perf.increment('mcpInitRan');
        log.info(`[MCP_INIT] Checking ${connected.length} connected MCP servers`);

        for (const entity of connected) {
          const entityId = entity._id || entity.id;
          try {
            const status = await mcpAdapter.getStatus(entityId);
            if (status.isActive) {
              const commands = mcpToolRegistry.registerConnection(
                entityId,
                entity.name,
                entity.skeleton.manifest,
                entity.skeleton.settings
              );
              registerSelfContainedCommands(commands);
              perf.increment('mcpToolsRegistered', commands.length);
              log.info(`[MCP_INIT] ✅ ${entity.name}: ${commands.length} tools registered`);
            } else {
              log.info(`[MCP_INIT] ${entity.name}: session gone, marking disconnected`);
              useEntityStore.getState().actions?.updateEntity?.(entityId, {
                'skeleton.status': 'disconnected',
              } as any);
              if (entity.skeleton?.settings?.autoReconnect) {
                log.info(`[MCP_INIT] ${entity.name}: autoReconnect triggered`);
                mcpAdapter.connect(entityId).then(({ manifest }) => {
                  if (manifest) {
                    const commands = mcpToolRegistry.registerConnection(
                      entityId, entity.name, manifest, entity.skeleton.settings
                    );
                    registerSelfContainedCommands(commands);
                  }
                }).catch((err) => {
                  log.warn(`[MCP_INIT] autoReconnect failed for ${entity.name}:`, err);
                });
              }
            }
          } catch (err) {
            log.warn(`[MCP_INIT] failed to check ${entity.name}:`, err);
          }
        }
      } catch (err) {
        log.warn('[MCP_INIT] initialization error (non-fatal):', err);
      }
    };

    // Subscribe to entityStore lifecycle; run MCP init once entityStore is 'ready'.
    // This replaces the old pattern of reading entities at mount time (before they load).
    let ran = false;
    import('./stores/entityStore').then(({ default: useEntityStore }) => {
      // Fast path: already ready when this effect runs (e.g. HMR reload)
      if (useEntityStore.getState().lifecycle === 'ready') {
        ran = true;
        runMCPInit();
        return;
      }
      const unsub = useEntityStore.subscribe((state) => {
        if (state.lifecycle === 'ready' && !ran) {
          ran = true;
          unsub();
          runMCPInit();
        }
      });
    }).catch(() => {});
  }, []);
  
  // Admin status now initialized in the parallel init effect above.

  // Handle URL-based deep linking to settings (works from ANY page including landing)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const settingsParam = urlParams.get('settings');
    const sectionParam = urlParams.get('section');
    
    if (settingsParam) {
      log.log('[App] 🔗 Detected settings URL deep link:', { tab: settingsParam, section: sectionParam });
      
      // Set state to open modal (works even on landing page without SystemMenuButton)
      setSettingsInitialTab(settingsParam);
      setSettingsInitialSection(sectionParam || undefined);
      setSettingsModalOpen(true);
      
      // Also dispatch event for pages that DO have SystemMenuButton
      window.dispatchEvent(new CustomEvent('openSettings', {
        detail: {
          tab: settingsParam,
          sectionId: sectionParam
        }
      }));
      
      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('settings');
      newUrl.searchParams.delete('section');
      window.history.replaceState({}, '', newUrl);
      
      log.log('[App] ✅ Settings modal opened via URL deep link, URL cleaned');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Initialize navigation prevention to protect against accidental navigation
  React.useEffect(() => {
    enableNavigationPrevention({
      enableGestureBlocking: true,
      enableBeforeUnloadWarning: true,
      enableKeyboardShortcuts: true,
      customWarningMessage: 'You have unsaved changes in your documents. Are you sure you want to leave?'
    });

    log.log('[App] Navigation prevention enabled');
  }, []);

  // Registries now initialized in the parallel init effect above.

  // Analytics init: runs once on mount. GA4 is synchronous; Mixpanel polls until ready
  // then re-identifies the current user. The interval is cleaned up on unmount.
  useEffect(() => {
    perf.increment('analyticsInitCount');
    const log = createFileLogger('APP_INIT');

    // Start Mixpanel initialization (async, non-blocking)
    (async () => {
      try {
        // Purge the Mixpanel recording DB before init if it has grown too large.
        // This must happen before mixpanel opens a connection, otherwise the
        // deleteDatabase request will be blocked.
        await new Promise<void>((resolve) => {
          const openReq = indexedDB.open('mixpanelBrowserDb');
          openReq.onsuccess = () => {
            const db = openReq.result;
            const storeName = 'mixpanelRecordingEvents';
            if (!db.objectStoreNames.contains(storeName)) { db.close(); resolve(); return; }
            const countReq = db.transaction(storeName, 'readonly').objectStore(storeName).count();
            countReq.onsuccess = () => {
              const count = countReq.result;
              db.close();
              if (count > 300) {
                log.warn(`Mixpanel DB has ${count} recording events — purging oversized buffer`);
                const delReq = indexedDB.deleteDatabase('mixpanelBrowserDb');
                delReq.onsuccess = () => resolve();
                delReq.onerror = () => resolve();
                delReq.onblocked = () => resolve();
              } else {
                resolve();
              }
            };
            countReq.onerror = () => { db.close(); resolve(); };
          };
          openReq.onerror = () => resolve();
        });

        const mixpanelConfig = await getMixpanelConfig();
        if (mixpanelConfig) {
          await mixpanelService.init(mixpanelConfig);
          log.debug('Mixpanel initialized successfully');
        }
      } catch (error) {
        log.error('Failed to initialize Mixpanel:', error);
      }
    })();

    try {
      ga4Service.init();
      log.debug('GA4 initialized successfully');
    } catch (error) {
      log.error('Failed to initialize GA4:', error);
    }

    // Poll until Mixpanel is ready, then re-identify the user for session recording.
    // userTrackingService.init() was already called once in the mount effect above;
    // we only call it here on the timeout path as a safety net.
    let attempts = 0;
    const maxAttempts = 12;

    perf.increment('mixpanelPollStartCount');
    const pollForMixpanel = setInterval(() => {
      perf.increment('mixpanelPollFireCount');
      attempts++;

      if (mixpanelService.getInitializationStatus()) {
        perf.increment('mixpanelPollClearedCount');
        clearInterval(pollForMixpanel);
        log.debug('Mixpanel ready, re-identifying user for session recording');

        const currentUserId = userTrackingService.getCurrentUserId();
        const userType = userTrackingService.getUserType();

        if (currentUserId && currentUserId !== 'unknown' && userType === 'authenticated') {
          mixpanelService.identify(currentUserId);
          mixpanelService.ensureRecordingActive();
          ga4Service.identify(currentUserId);
          log.info('✅ Re-identified authenticated user and activated recording:', currentUserId);
        } else if (userType === 'anonymous') {
          log.debug('⏭️ Skipping recording for anonymous user (will activate after login/signup)');
        }

      } else if (attempts >= maxAttempts) {
        perf.increment('mixpanelPollClearedCount');
        clearInterval(pollForMixpanel);
        log.warn('Mixpanel initialization timeout, proceeding without analytics');
        // Safety net: ensure tracking is initialized even if Mixpanel never loaded
        perf.increment('userTrackingInitCount');
        userTrackingService.init();
      }
    }, 5000);

    devOnly(() => {
      import('./utils/consoleDebugCommands').then(({ registerConsoleDebugCommands }) => {
        registerConsoleDebugCommands();
      }).catch(() => {});

      import('./utils/debugDataspace').then(() => {
        console.log('🔍 Dataspace debugging utilities loaded!');
        console.log('   Run: debugDataspace() - Full diagnostic report');
        console.log('   Run: clearEntityCache() - Clear all entity caches');
        console.log('   Run: reloadEntities() - Force reload entities from backend');
        console.log('   Run: switchToOrg("orgId") - Switch to org dataspace');
      }).catch(() => {});
    });

    import('./utils/lazyImports').then(({ preloadCommonModules }) => {
      preloadCommonModules();
    }).catch(() => {});

    return () => {
      clearInterval(pollForMixpanel);
      mixpanelService.stopRecording();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider>
      <SystemContextProvider>
        <SelfHostedLicenseGate>
          <div className="app-container">
            <Suspense fallback={<Loader message="Loading River..." />}>
            <ScrollToTop />
            <Routes>
            {/* ==== MAIN APPLICATION ROUTES ==== */}
            <Route
              path="/spaces/:id"
              element={
                <ProtectedRoute>
                  <SpacePageWrapperWithKey />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new-space"
              element={
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <SpaceCreationPage
                      onClose={() => {
                        const riverOS = (window as any).riverOS;
                        if (riverOS?.window?.close) {
                          riverOS.window.close();
                        } else {
                          window.history.back();
                        }
                      }}
                    />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomeSpaceWrapper />
                </ProtectedRoute>
              }
            />

            {/* ==== STANDALONE APP ROUTES ==== */}
            <Route
              path="/app/agent-panel"
              element={
                <AgentLayout>
                  <AgentPanelRoot />
                </AgentLayout>
              }
            />
            <Route
              path="/app/vault"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Vault">
                      <PasswordVaultApp windowId="standalone-vault" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/llmchef"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="LLM Chef">
                      <LLMChefApp windowId="standalone-llmchef" spaceId="default" onClose={() => {}} title="LLM Chef" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/listen"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Listen">
                      <ListenApp windowId="standalone-listen" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/mediagen"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Media Gen">
                      <MediaGenApp windowId="standalone-mediagen" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/promptlibrary"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Prompt Library">
                      <PromptLibraryApp windowId="standalone-promptlibrary" spaceId="default" onClose={() => {}} title="Prompt Library" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/inbox"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Inbox">
                      <InboxApp windowId="standalone-inbox" spaceId="default" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/quicklauncher"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Quick Launcher">
                      <QuickLauncherApp windowId="standalone-quicklauncher" spaceId="default" onClose={() => {}} title="Quick Launcher" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/store"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Store">
                      <StoreApp windowId="standalone-store" spaceId="default" onClose={() => {}} title="Store" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/docs/:docId?"
              element={
                <GUILayout pageType="standalone">
                  <StandaloneAppLayout appTitle="River">
                    <DocumentAccessWrapper windowId="standalone-docs" spaceId="default" onClose={() => {}} title="Docs" />
                  </StandaloneAppLayout>
                </GUILayout>
              }
            />
            <Route
              path="/standalone/:entityId"
              element={
                <ProtectedRoute>
                  <StandaloneEntityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/entity/:entityId"
              element={
                <ProtectedRoute>
                  <StandaloneEntityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/chat"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Chat">
                      <ChatApp windowId="standalone-chat" spaceId="default" onClose={() => {}} title="Chat" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/entitybrowser2"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Entity Browser Pro">
                      <RiverDriveApp 
                        windowId="standalone-entitybrowser2" 
                        spaceId="default" 
                        onClose={() => {}} 
                        title="Entity Browser Pro" 
                      />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/banking"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Banking">
                      <BankApp 
                        onClose={() => {}} 
                        title="Banking"
                        windowId="standalone-banking-window" 
                        spaceId="standalone-space" 
                      />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/docs"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Documents">
                      <DocumentAccessWrapper windowId="standalone-docs" spaceId="default" onClose={() => {}} title="Documents" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/tasks"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Tasks" appId="tasks">
                      <TasksApp windowId="standalone-tasks" spaceId="default" onClose={() => {}} title="Tasks" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/automations"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Automations" appId="automations">
                      <AutomationsApp windowId="standalone-automations" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/contacts"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Contacts" appId="contacts">
                      <StandaloneContactsApp />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/app/mail"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Mail" appId="mail">
                      <StandaloneMailAppWrapper />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/messaging"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Messages" appId="messaging">
                      <StandaloneMessagingAppWrapper />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/bookkeeper"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Bookkeeper">
                      <BookkeeperApp windowId="bookkeeper-standalone" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/ingestwizard"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Ingest Wizard">
                      <IngestWizardApp windowId="standalone-ingestwizard" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/notes/:noteId?"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <ElectronAppLayout>
                      <NotesMarkdownApp windowId="standalone-notes" spaceId="default" onClose={() => {}} />
                    </ElectronAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route path="/app/login" element={<LoginApp />} />
            
            {/* ==== INTEGRATION CALLBACKS (Public - no auth required) ==== */}
            <Route path="/app/settings/integrations/google-drive/callback" element={<GoogleDriveCallbackPage />} />
            <Route path="/oauth/gmail/callback" element={<GmailOAuthCallback />} />
            
            {/* Blog subdomain Auth0 callback - centralized handler for all blog.rivereditor.com subdomains */}
            <Route path="/blog-auth-callback" element={<BlogAuthCallback />} />

            {/* ==== AUTH & LANDING ROUTES ==== */}
            <Route path="/electron-onboarding" element={<ElectronOnboardingPage />} />
            {/* Cinematic first-run onboarding for new Electron installs — Test at: http://localhost:3000/electron-first-run */}
            <Route path="/electron-first-run" element={<ElectronFirstRunPage />} />
            {/* Agents home — list and resume past agent runs */}
            <Route
              path="/agents"
              element={
                <ProtectedRoute>
                  <AgentsHome />
                </ProtectedRoute>
              }
            />
            {/* Agent conversation page — URL carries threadId so reloads restore exact session */}
            <Route
              path="/agents/:threadId"
              element={
                <ProtectedRoute>
                  <AgentConversationPage />
                </ProtectedRoute>
              }
            />
            {/* Dev-only: onboarding card UI playground — http://localhost:3000/first-run-preview */}
            {IS_DEV && <Route path="/first-run-preview" element={<OnboardingCardPreview />} />}
            {/* Dev-only: tool card state inspector — http://localhost:3000/tool-card-preview */}
            {IS_DEV && <Route path="/tool-card-preview" element={<ToolCardPreview />} />}
            {/* Self-hosted onboarding with welcome animation - Test at: http://localhost:3000/selfhosted-onboarding */}
            <Route path="/selfhosted-onboarding" element={<SelfHostedOnboardingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/selfhosted-login" element={<SelfhostedLoginPage />} />
            {!([
              'bedrock.computer',
              'riversystem.ai',
              'rivereditor.com',
            ].some(d => window.location.hostname.includes(d))) ? (
              <Route path="/signup" element={<SignupPage />} />
            ) : null}
            
            {/* Affiliate referral entry point — single link for all audiences */}
            <Route path="/r/:code" element={<AffiliateRefRoute />} />

            {/* Root route: authenticated users see QuickLauncher with chat, others see landing */}
            <Route path="/" element={<RootRoute />} />
            {/* Document route from launcher - shares same layout as root */}
            <Route path="/d/:docId" element={<RootRoute />} />
            
            {/* Public Book Viewer — accessible without an account */}
            <Route path="/book/:entityId" element={<PublicBookViewer />} />

            {/* Showcase Routes - Public */}
            <Route path="/showcase/:userId" element={<ShowcasePage />} />
            <Route path="/showcase/:userId/doc/:docId" element={<ShowcaseDocumentPage />} />
            {/* LEGACY: /onboarding/* paths - keeping for backwards compatibility */}
            <Route path="/onboarding/nsfw" element={<NSFWOnboarding />} />
            <Route path="/onboarding/blog-creator" element={<BlogCreatorOnboarding />} />
            <Route path="/onboarding/seo-blog-creator" element={<SEOBlogCreatorOnboarding />} />
            <Route path="/onboarding/seo-optimizer" element={<SEOOptimizerOnboarding />} />
            <Route path="/onboarding/content-polisher" element={<ContentPolisherOnboarding />} />
            <Route path="/onboarding/image-inserter" element={<ImageInserterOnboarding />} />
            <Route path="/onboarding/:persona" element={<TemplateOnboarding />} />
            
            {/* NEW: /tools/* paths - preferred going forward */}
            <Route path="/tools/nsfw" element={<Navigate to="/nsfw" replace />} />
            <Route path="/tools/blog-creator" element={<BlogCreatorOnboarding />} />
            <Route path="/tools/seo-optimizer" element={<SEOOptimizerOnboarding />} />
            <Route path="/tools/content-polisher" element={<ContentPolisherOnboarding />} />
            <Route path="/tools/image-inserter" element={<ImageInserterOnboarding />} />
            <Route path="/academic-medicine-pi" element={<AcademicMedicinePINew />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/org-payment-success" element={<OrgPaymentSuccessPage />} />
            <Route path="/invitations/:token" element={<InvitationAcceptPage />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/nsfw" element={<NSFWAIWriter />} />
            <Route path="/beta-read" element={<ManuscriptFeedbackLanding />} />
            
            {/* Persona Pages - Organized under /personas/ for analytics bucketing */}
            <Route path="/personas/ai-grant-writer" element={<GrantWriters />} />
            <Route path="/personas/uncensored-ai-writer" element={<Navigate to="/nsfw" replace />} />
            <Route path="/personas/private-workspace" element={<PrivateWorkspace />} />
            <Route path="/personas/creative-writing-ai" element={<CreativeFreedom />} />
            <Route path="/personas/ai-fanfiction-writer" element={<FanfictionWriter />} />
            <Route path="/addons/self-published-book" element={<SelfPublishedBookRedirect />} />
            <Route path="/addons/self-published-book/purchase" element={<BookAddonPurchasePage />} />
            <Route path="/personas/ai-rfp-writer" element={<RFPWriters />} />
            <Route path="/personas/ai-for-research-writing" element={<AcademicWriters />} />
            <Route path="/personas/family-office-ai-writer" element={<HNWWriters />} />
            
            {/* Persona Pages - Legacy redirects (keep old links working) */}
            <Route path="/grant-writing" element={<Navigate to="/personas/ai-grant-writer" replace />} />
            <Route path="/ai-grant-writer" element={<Navigate to="/personas/ai-grant-writer" replace />} />
            <Route path="/uncensored-ai-writer" element={<Navigate to="/nsfw" replace />} />
            <Route path="/private-ai-workspace" element={<Navigate to="/personas/private-workspace" replace />} />
            <Route path="/private-workspace" element={<Navigate to="/personas/private-workspace" replace />} />
            <Route path="/ai-creative-freedom" element={<Navigate to="/personas/creative-writing-ai" replace />} />
            <Route path="/creative-writing-ai" element={<Navigate to="/personas/creative-writing-ai" replace />} />
            <Route path="/fanfiction-ai-writer" element={<Navigate to="/personas/ai-fanfiction-writer" replace />} />
            <Route path="/ai-fanfiction-writer" element={<Navigate to="/personas/ai-fanfiction-writer" replace />} />
            
            {/* Tool Routes */}
            <Route path="/humanize-ai" element={<AIHumanizerLanding />} />
            <Route path="/onboarding/humanizer" element={<HumanizerOnboarding />} />
            <Route path="/onboarding/welcome" element={<RiverOnboarding />} />
            
            {/* Space Templates Landing */}
            <Route path="/space-templates" element={<RiverSpacesLanding />} />

            {/* Product Routes */}
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/river4" element={<River4 />} />
            <Route path="/human-first" element={<HumanFirst />} />
            <Route path="/ai-personal-computer" element={<AiPersonalComputer />} />
            <Route path="/personal-ai" element={<PersonalAIPage />} />
            <Route path="/river-config" element={<RiverConfigFlow />} />
            <Route path="/work" element={<WorkLanding />} />
            <Route path="/chief-of-staff" element={<Navigate to="/work" replace />} />
            <Route path="/concierge" element={<ConciergeRiverLanding />} />
            <Route
              path="/work-landing-upgrade"
              element={
                <ProtectedRoute>
                  <WorkLandingUpgradePage />
                </ProtectedRoute>
              }
            />
            <Route path="/workspace" element={<WorkspacePage />} />
            <Route path="/tasks" element={<TasksAppPage />} />
            <Route path="/os-signup" element={<OSSignupFlow />} />
            <Route path="/configure-river-1" element={<ConfigureRiver1Page />} />
            <Route path="/checkout" element={<RiverConfigFlow />} />
            <Route path="/checkout-old" element={<CheckoutPage />} />
            
            {/* Enterprise Routes */}
            <Route path="/enterprise/science" element={<AcademicMedicinePINew />} />
            <Route path="/enterprise/law" element={<LawyerDocsEditor />} />
            <Route path="/enterprise/consulting" element={<ConsultantDocsEditor />} />

            {/* Use Case Routes */}
            <Route path="/use-cases/prelaw-deadline-panic" element={<PreLawDeadlinePanic />} />

            {/* Converter Routes */}
            <Route path="/convert/pdf-to-word" element={<PdfToWordConverter />} />
            <Route path="/manuscript-feedback" element={<ManuscriptFeedbackLanding />} />
            <Route path="/manuscript-feedback/discover/:jobId" element={<ManuscriptDiscovery />} />
            <Route path="/manuscript-feedback/analyzing/:jobId" element={<ManuscriptAnalysisProgress />} />
            <Route path="/convert/chatgpt-to-word" element={<ChatGPTToWordConverter />} />
            <Route path="/convert/markdown-to-word" element={<MarkdownToWordConverter />} />

            {/* Tool Routes */}
            <Route path="/tools/youtube-script-creator" element={<YouTubeScriptCreator />} />
            <Route path="/tools/ai-product-description-generator" element={<AIProductDescriptionGenerator />} />
            <Route path="/tools/manuscript-feedback" element={<ManuscriptFeedbackLanding />} />
            <Route path="/tools/copyeditor" element={<CopyeditorLanding />} />
            <Route path="/tools/essay" element={<EssayReviewLanding />} />
            
            {/* Tools Landing Page - Must come BEFORE dynamic slug route */}
            <Route path="/tools" element={<ToolsLandingPage />} />
            
            {/* Workflow Input Page - Must come BEFORE dynamic slug route */}
            <Route path="/tools/:slug/app" element={<WorkflowInputPage />} />
            
            {/* NEW: Workflow Landing Pages with dynamic slug */}
            <Route path="/tools/:slug" element={<WorkflowLanding />} />
            
            {/* SEO Blogs Landing Page - For the new blog creation system */}
            <Route path="/seo-blogs" element={<SEOBlogsLanding />} />
            
            {/* Blogs Landing Page - Must come BEFORE dynamic slug route */}
            <Route path="/blogs" element={<BlogsLandingPage />} />
            
            {/* Blog Post Pages with dynamic slug */}
            <Route path="/blogs/:slug" element={<BlogLanding />} />
            
            {/* Guide Tool Pages - SPA routes for interactive tools */}
            <Route path="/guides/:slug/app" element={<WorkflowInputPage />} />
            
            {/* Getting Started Guide */}
            <Route path="/getting-started" element={<GettingStartedPage />} />
            
            {/* For [Audience] Pages */}
            <Route path="/for/ghostwriters" element={<GhostwriterLanding />} />
            <Route path="/for/grant-writers" element={<GrantWriterLanding />} />
            <Route path="/for/marketers" element={<MarketersLanding />} />
            
            <Route path="/fact-checker" element={<FactCheckerLanding />} />
            {/* Legacy redirects for backwards compatibility */}
            <Route path="/essay" element={<Navigate to="/tools/essay" replace />} />
            <Route path="/collegeapp" element={<CollegeEssayLanding />} />
            <Route path="/college-essay" element={<Navigate to="/tools/essay" replace />} />

            {/* Company Routes */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Avatar — public orb demos */}
            <Route path="/avatar"  element={<AvatarPage />} />
            <Route path="/avatar2" element={<AvatarPage2 />} />
            <Route path="/avatar3" element={<AvatarPage3 />} />

            {/* ==== ADMIN ROUTES ==== */}
            <Route
              path="/admin"
              element={
                <GUILayout pageType="standalone">
                  <StandaloneAppLayout appTitle="Admin Setup">
                    <AdminSetup />
                  </StandaloneAppLayout>
                </GUILayout>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Admin Dashboard">
                      <AdminDashboard />
                    </StandaloneAppLayout>
                  </GUILayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Admin Analytics">
                      <AdminAnalyticsView />
                    </StandaloneAppLayout>
                  </GUILayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/llm-usage"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="LLM Usage">
                      <AdminLLMUsageTable />
                    </StandaloneAppLayout>
                  </GUILayout>
                </AdminProtectedRoute>
              }
            />

            {/* ==== PUBLIC ROUTES (no auth required) ==== */}
            <Route
              path="/broadcast-unsubscribe"
              element={<BroadcastUnsubscribePageLazy />}
            />

            {/* ==== FALLBACK ROUTES ==== */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>

        {/* Global Upgrade Modal with Tier Selection */}
        <UpgradeModal />

        {/* Invite-a-Friend Modal (free users, 100-credit threshold) — disabled */}
        {/* {showInviteFriendModal && (
          <Suspense fallback={null}>
            <InviteFriendModalLazy onClose={() => setShowInviteFriendModal(false)} subtitle="Get free usage — share River and instantly unlock:" />
          </Suspense>
        )} */}

        {/* Global Tool Consent Modal */}
        <ToolConsentModal />

        {/* Toast containers for all positions actively used in the codebase */}
        <ToastContainer position="top-right" maxToasts={5} />
        <ToastContainer position="top-center" maxToasts={3} />
        <ToastContainer position="bottom-right" maxToasts={3} />
        <ToastContainer position="center" maxToasts={3} />

        <ToastStack />

        {/* Window controls for frameless Electron on Linux/Windows */}
        <WindowControls />

        {/* Auto-update toast notification */}
        <React.Suspense fallback={null}>
          <UpdateToast />
        </React.Suspense>
        
        {/* Session Expired Modal */}
        <SessionExpiredModal
          isOpen={isSessionExpiredModalOpen}
          onLogin={handleLogin}
        />

        {/* RiverOS v1 Purchase Modal */}
        <OSSignupModal />
        
        {/* Org Payment Gate - Blocks unpaid orgs */}
        <OrgPaymentGateWrapper />
        
        {/* Global Settings Modal (for URL deep linking from landing pages) */}
        {settingsModalOpen && (
          <React.Suspense fallback={<div />}>
            <UserSettingsModal
              isOpen={settingsModalOpen}
              onClose={() => {
                setSettingsModalOpen(false);
                setSettingsInitialTab(undefined);
                setSettingsInitialSection(undefined);
              }}
              initialTab={settingsInitialTab as any}
              initialSectionId={settingsInitialSection}
            />
          </React.Suspense>
        )}
        
        {/* Global Contextual AI Modal — opened by Ambient Intelligence Surface */}
        {GlobalCmdKModal}

        {/* Global Entity Search — Cmd+K */}
        {GlobalEntitySearchModal}

        {/* Ambient Intelligence Surface — reactive suggestions triggered by world events */}
        {AmbientBubble}
        
        {/* Y.js Debug Panel — toggle with Ctrl+Shift+Y */}
        <React.Suspense fallback={null}>
          <YjsDebugPanel />
        </React.Suspense>

          </div>
        </SelfHostedLicenseGate>
      </SystemContextProvider>
    </ThemeProvider>
  );
}

export default App;
